import { FaHome, FaUserFriends, FaCog, FaBell, FaEnvelope, FaPlus, FaPen, FaSearch } from "react-icons/fa";
import { useState, useEffect, useCallback } from "react";
import styles from "./menu.module.css";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../Utils/api";
import { useNotifications } from '../hooks/useNotifications';
import { useConversationsList } from '../hooks/useConversations';

const Menu = ({ pp, setActive, active, onPublish, socket, setToast }) => {
  const navigate  = useNavigate();
  const token     = localStorage.getItem('token');
  const userId    = localStorage.getItem('userId');

  const [isMobile, setIsMobile]       = useState(() =>
    /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || window.innerWidth <= 768
  );

  const { data: notifsRes = [], refetch: refetchNotifs } = useNotifications(token, userId);
  const { data: convsRes = [], refetch: refetchConvs } = useConversationsList(token);

  // Automatically hide badge if the user is already on that tab
  const displayNotifs = active === 'notifications' ? 0 : notifsRes.filter(n => !n.isRead).length;
  const displayMsg = active === 'messenger' ? 0 : convsRes.reduce((sum, c) => sum + (c.lastMessage?.unread || 0), 0);

  /* ── Responsive ── */
  useEffect(() => {
    const check = () =>
      setIsMobile(/Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || window.innerWidth <= 768);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  /* ── Mise à jour temps réel via socket ── */
  useEffect(() => {
    if (!socket) return;

    const onNewMsg = (msg) => {
      refetchConvs();
      if (setToast && active !== 'messenger') {
        const text = msg?.message || msg?.text || 'Message';
        setToast(`Nouveau message: ${text.length > 30 ? text.substring(0, 30) + '...' : text}`);
      }
    };
    const onNewNotif = (notif) => {
      refetchNotifs();
      if (setToast && notif && active !== 'notifications') {
        setToast(`${notif.username} ${notif.message}`);
      }
    };

    socket.on('newMsg',      onNewMsg);
    socket.on('message',     onNewMsg);
    socket.on('newNotif',    onNewNotif);
    socket.on('notification',onNewNotif);

    return () => {
      socket.off('newMsg',      onNewMsg);
      socket.off('message',     onNewMsg);
      socket.off('newNotif',    onNewNotif);
      socket.off('notification',onNewNotif);
    };
  }, [socket, refetchConvs, refetchNotifs, active, setToast]);

  /* ── Navigation ── */
  const handleNav = useCallback((item) => {
    if (!setActive) return;
    // Items with a permanent link always navigate (e.g. settings)
    if (item.link) {
      navigate(item.link);
      return;
    }
    // Items with mobileLink navigate only on mobile, otherwise just change active
    if (isMobile && item.mobileLink) {
      navigate(item.mobileLink);
      return;
    }
    setActive(item.id);
  }, [setActive, isMobile, navigate]);

  /* ── Items ── */
  const desktopItems = [
    { id: 'home',          label: 'Accueil',  icon: <FaHome />        },
    { id: 'messenger',     label: 'Messages', icon: <FaEnvelope />,   badge: displayMsg },
    { id: 'amis',          label: 'Amis',     icon: <FaUserFriends /> },
    { id: 'notifications', label: 'Notifs',   icon: <FaBell />,       badge: displayNotifs  },
  ];
  const bottomItems = [
    { id: 'settings', label: 'Paramètres', icon: <FaCog />, link: '/settings' },
  ];
  const mobileItems = [
    { id: 'home',          label: 'Accueil',  icon: <FaHome />,        mobileLink: '/home'          },
    { id: 'Découvrir',          label: 'Découvrir',     icon: <FaSearch />, mobileLink: '/search'       },
    { id: 'messenger',     label: 'Messages', icon: <FaEnvelope />,    mobileLink: '/messenger',    badge: displayMsg },
    { id: 'notifications', label: 'Notifs',   icon: <FaBell />,        mobileLink: '/notifications', badge: displayNotifs  },
  ];

  /* ── Render item desktop ── */
  const renderNavItem = (item) => (
    <div
      key={item.id}
      className={`${styles.menuItem} ${active === item.id ? styles.active : ''}`}
      onClick={() => handleNav(item)}
      role="button"
      tabIndex={0}
      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && handleNav(item)}
    >
      <span className={styles.iconWrap}>
        <span className={styles.icon}>{item.icon}</span>
        {item.badge > 0 && (
          <span className={styles.badge}>
            {item.badge > 99 ? '99+' : item.badge}
          </span>
        )}
      </span>
      <span className={styles.label}>{item.label}</span>
    </div>
  );

  const publishBtn = (
    <button className={styles.publishButton} onClick={onPublish} title="Publier">
      <FaPen />
      <span className={styles.publishButtonLabel}> Publier</span>
    </button>
  );

  /* ── Desktop ── */
  if (!isMobile) return (
    <aside className={styles.menu}>
      <div className={styles.menuContent}>
        <nav className={styles.mainNav}>
          {desktopItems.map(renderNavItem)}
          {publishBtn}
        </nav>
        <nav className={styles.bottomNav}>
          {bottomItems.map(item => (
            <div
              key={item.id}
              className={`${styles.menuItem} ${active === item.id ? styles.active : ''}`}
              onClick={() => handleNav(item)}
              role="button"
              tabIndex={0}
            >
              <span className={styles.icon}>{item.icon}</span>
              <span className={styles.label}>{item.label}</span>
            </div>
          ))}
        </nav>
      </div>
    </aside>
  );

  /* ── Mobile ── */
  return (
    <nav className={styles.mobileNav}>
      {mobileItems.slice(0, 2).map(item => (
        <button
          key={item.id}
          className={`${styles.mobileNavItem} ${active === item.id ? styles.mobileActive : ''}`}
          onClick={() => handleNav(item)}
        >
          <span className={styles.mobileIconWrap}>
            <span className={styles.mobileIcon}>{item.icon}</span>
            {item.badge > 0 && (
              <span className={styles.mobileBadge}>
                {item.badge > 99 ? '99+' : item.badge}
              </span>
            )}
          </span>
          <span className={styles.mobileLabel}>{item.label}</span>
        </button>
      ))}

      {active === 'home' && (
        <button className={styles.mobilePublishBtn} onClick={onPublish} title="Publier">
          <FaPlus />
        </button>
      )}

      {mobileItems.slice(2).map(item => (
        <button
          key={item.id}
          className={`${styles.mobileNavItem} ${active === item.id ? styles.mobileActive : ''}`}
          onClick={() => handleNav(item)}
        >
          <span className={styles.mobileIconWrap}>
            <span className={styles.mobileIcon}>{item.icon}</span>
            {item.badge > 0 && (
              <span className={styles.mobileBadge}>
                {item.badge > 99 ? '99+' : item.badge}
              </span>
            )}
          </span>
          <span className={styles.mobileLabel}>{item.label}</span>
        </button>
      ))}
    </nav>
  );
};

export default Menu;